import React, { useState } from 'react';
import { RoleSettings as RoleSettingsType, RoleVisibility } from '../types';
import '../styles/RoleSettings.css';

interface RoleSettingsProps {
  settings: RoleSettingsType;
  onSettingsChange: (settings: RoleSettingsType) => void;
}

const ROLE_LABELS: Record<keyof RoleVisibility, string> = {
  canCreateProjects: 'Can Create Projects',
  canEditProjects: 'Can Edit Projects',
  canDeleteProjects: 'Can Delete Projects',
  canExportPDF: 'Can Export to PDF',
  canViewMaterials: 'Can View Materials',
  canViewProcesses: 'Can View Processes',
  canViewCosts: 'Can View Costs',
  canViewMarkupDetails: 'Can View Markup Details',
  canViewAllProjects: 'Can View All Projects',
  canViewOnlyOwnProjects: 'Can View Only Own Projects',
  canManageUsers: 'Can Manage Users',
  canManageSettings: 'Can Manage Settings',
};

const ROLE_DESCRIPTIONS: Record<keyof RoleVisibility, string> = {
  canCreateProjects: 'Allow creating new projects',
  canEditProjects: 'Allow editing existing projects',
  canDeleteProjects: 'Allow deleting projects',
  canExportPDF: 'Allow exporting quotes to PDF',
  canViewMaterials: 'Allow viewing materials list',
  canViewProcesses: 'Allow viewing processes and modifications',
  canViewCosts: 'Allow viewing cost information',
  canViewMarkupDetails: 'Allow viewing markup percentages',
  canViewAllProjects: 'Allow viewing projects from all team members',
  canViewOnlyOwnProjects: 'Restrict viewing to only their own projects',
  canManageUsers: 'Allow managing user accounts',
  canManageSettings: 'Allow managing company settings',
};

export const RoleSettings: React.FC<RoleSettingsProps> = ({
  settings,
  onSettingsChange,
}) => {
  const [expandedRole, setExpandedRole] = useState<'admin' | 'sales' | 'viewer' | null>('admin');

  const togglePermission = (role: 'admin' | 'sales' | 'viewer', key: keyof RoleVisibility) => {
    const updatedSettings = {
      ...settings,
      [role]: {
        ...settings[role],
        [key]: !settings[role][key],
      },
    };
    onSettingsChange(updatedSettings);
  };

  const getRoleColor = (role: 'admin' | 'sales' | 'viewer') => {
    switch (role) {
      case 'admin':
        return '#667eea';
      case 'sales':
        return '#10b981';
      case 'viewer':
        return '#f59e0b';
      default:
        return '#667eea';
    }
  };

  const roles: ('admin' | 'sales' | 'viewer')[] = ['admin', 'sales', 'viewer'];

  return (
    <div className="role-settings">
      <div className="role-header">
        <h3>User Role Permissions</h3>
        <p className="role-desc">Configure what each user role can access and do in the system</p>
      </div>

      <div className="roles-container">
        {roles.map((role) => (
          <div key={role} className="role-card">
            <div
              className="role-card-header"
              onClick={() => setExpandedRole(expandedRole === role ? null : role)}
              style={{ cursor: 'pointer' }}
            >
              <div
                className="role-badge"
                style={{
                  backgroundColor: getRoleColor(role),
                }}
              >
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </div>
              <span className="role-name">{role.charAt(0).toUpperCase() + role.slice(1)} Role</span>
              <span className="expand-icon">
                {expandedRole === role ? '▼' : '▶'}
              </span>
            </div>

            {expandedRole === role && (
              <div className="role-permissions">
                {Object.entries(ROLE_LABELS).map(([key, label]) => {
                  const permissionKey = key as keyof RoleVisibility;
                  const isChecked = settings[role][permissionKey];

                  return (
                    <div key={key} className="permission-item">
                      <label className="permission-label">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => togglePermission(role, permissionKey)}
                        />
                        <span className="permission-text">
                          <strong>{label}</strong>
                          <span className="permission-desc">
                            {ROLE_DESCRIPTIONS[permissionKey]}
                          </span>
                        </span>
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary table */}
      <div className="role-summary">
        <h4>Permissions Summary</h4>
        <div className="summary-table">
          <div className="summary-header">
            <div className="summary-cell permission-name">Permission</div>
            <div className="summary-cell" style={{ color: getRoleColor('admin') }}>Admin</div>
            <div className="summary-cell" style={{ color: getRoleColor('sales') }}>Sales</div>
            <div className="summary-cell" style={{ color: getRoleColor('viewer') }}>Viewer</div>
          </div>
          {Object.entries(ROLE_LABELS).map(([key, label]) => {
            const permissionKey = key as keyof RoleVisibility;
            return (
              <div key={key} className="summary-row">
                <div className="summary-cell permission-name">{label}</div>
                <div className="summary-cell">
                  {settings.admin[permissionKey] ? '✓' : '—'}
                </div>
                <div className="summary-cell">
                  {settings.sales[permissionKey] ? '✓' : '—'}
                </div>
                <div className="summary-cell">
                  {settings.viewer[permissionKey] ? '✓' : '—'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
